
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, LinearProgress, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Card, Divider, Avatar, Stack, useTheme, alpha, TablePagination, Snackbar, Alert } from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon, Edit as EditIcon, Warning as WarningIcon, Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { getData, putData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import Loader from '../../../common/spinner';

const leaveTypeColors = {
    'Sick Leave': '#f44336',
    'Casual Leave': '#2196f3',
    'Earned Leave': '#4caf50',
    'Maternity Leave': '#9c27b0',
    'Paternity Leave': '#673ab7',
};

const EmployeesLeaveBalanceList = () => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData?.organization_id;
    const theme = useTheme();
    const [employees, setEmployees] = useState([]);
    const [organizationId, setOrganizationId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('All');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentEmployee, setCurrentEmployee] = useState(null);
    const [currentLeaveType, setCurrentLeaveType] = useState('');
    const [currentLeaveBalance, setCurrentLeaveBalance] = useState({
        total: 0,
        used: 0,
        pending: 0,
        remaining: 0
    });
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState(['All']);
    const [error, setError] = useState(null);
    const [organizationLeaveTypes, setOrganizationLeaveTypes] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [selectedLeaveTypes, setSelectedLeaveTypes] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleLeaveTypeSelect = (employeeId, leaveType) => {
        setSelectedLeaveTypes(prev => ({
            ...prev,
            [employeeId]: leaveType
        }));
    };
    const getSelectedLeaveBalance = (employee) => {
        const selectedType = selectedLeaveTypes[employee.id];
        return employee.leaveBalances.find(balance => balance.type === selectedType) ||
            employee.leaveBalances[0] ||
            { type: 'No Leave', total: 0, used: 0, pending: 0, remaining: 0 };
    };

    const fetchLeaveTypes = useCallback(async (employeeUserId) => {
        try {
            const userId = employeeUserId || localStorage.getItem('userId');
            if (!userId) return;

            const config = API_ENDPOINTS.getAllLeaveTypesByEmployeeId(userId);
            const response = await getData(config);

            if (response?.status === 'SUCCESS') {
                setOrganizationLeaveTypes(response?.message?.leaveTypesWithBalance);
            }
        } catch (err) {
            return null;
        }
    }, []);

    const fetchLeaveBalances = useCallback(async (targetOrgId) => {
        setLoading(true);
        setError(null);
        try {
            // Use the passed orgId or the state fallback
            const response = await getData(API_ENDPOINTS.getAllLeaveBalancesByOrganizationId(targetOrgId || organizationId));
            if (response?.status === 'SUCCESS') {
                const transformedData = transformApiData(response?.result);
                setEmployees(transformedData);
                const uniqueDepartments = ['All', ...new Set(transformedData.map(emp => emp.department))];
                setDepartments(uniqueDepartments);
            }
        } catch (err) {
            setError("Failed to fetch balances");
        } finally {
            setLoading(false);
        }
    }, [organizationId]); // Add organizationId if the function depends on the state variable

    useEffect(() => {
        if (orgId) {
            setOrganizationId(orgId);
            fetchLeaveBalances(orgId);
            fetchLeaveTypes();
        }
    }, [orgId, fetchLeaveBalances, fetchLeaveTypes]);

    const transformApiData = (apiData) => {
        return apiData.map(employee => {
            const nameParts = employee.employee_name.split(' ');
            const avatar = nameParts
                .slice(0, 2)
                .map(part => part[0]?.toUpperCase() || '')
                .join('');

            const leaveBalances = employee.leave_types.map(leaveType => {
                if (!leaveType.leave_type) {
                    return {
                        type: 'No Leave Assigned',
                        total: 0,
                        used: 0,
                        pending: 0,
                        remaining: 0,
                        leave_type_id: null
                    };
                }

                return {
                    type: leaveType.leave_type,
                    total: leaveType.total,
                    used: leaveType.used_days,
                    pending: 0,
                    remaining: leaveType.remaining_days,
                    leave_type_id: leaveType.leave_type_id
                };
            });

            const filteredLeaveBalances = leaveBalances.filter(lb => lb.type !== 'No Leave Assigned' || leaveBalances.length === 1);

            return {
                id: employee.user_id,
                name: employee.employee_name,
                // employeeId: `EMP${employee.user_id}`,
                employeeId: employee.emp_id,
                department: employee.department,
                avatar: avatar,
                leaveBalances: filteredLeaveBalances
            };
        });
    };

    const handleLeaveTypeChange = (e) => {
        const selectedType = e.target.value;

        const leaveBalance = currentEmployee.leaveBalances.find(lb => lb.type === selectedType);
        if (leaveBalance) {
            setCurrentLeaveType(selectedType);
            setCurrentLeaveBalance(leaveBalance);
        } else {
            const orgLeaveType = organizationLeaveTypes.find(lt => lt.leave_type === selectedType);
            if (orgLeaveType) {
                setCurrentLeaveType(selectedType);
                setCurrentLeaveBalance({
                    type: selectedType,
                    leave_type_id: orgLeaveType.id,
                    total: orgLeaveType.max_days || 0,
                    used: 0,
                    pending: 0,
                    remaining: orgLeaveType.max_days || 0
                });
            } else {
                setCurrentLeaveType(selectedType);
                setCurrentLeaveBalance({
                    type: selectedType,
                    total: 0,
                    used: 0,
                    pending: 0,
                    remaining: 0
                });
            }
        }
    };

    const filteredEmployees = employees.filter(employee => {
        const isNameMatch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase());
        const isDepartmentMatch = selectedDepartment === 'All' || employee.department === selectedDepartment;
        return isNameMatch && isDepartmentMatch;
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(0);
    };

    const handleDepartmentChange = (e) => {
        setSelectedDepartment(e.target.value);
        setPage(0);
    };

    const handleOpenDialog = (employee) => {
        setCurrentEmployee(employee);
        setCurrentLeaveType(employee.leaveBalances[0]?.type || '');
        setCurrentLeaveBalance(employee.leaveBalances[0] || {
            total: 0,
            used: 0,
            pending: 0,
            remaining: 0
        });


        fetchLeaveTypes(employee.id);

        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleBalanceChange = (e) => {
        const { name, value } = e.target;
        const numValue = parseFloat(value) || 0;
        let newBalance = { ...currentLeaveBalance, [name]: numValue };
        if (name === 'total' || name === 'used' || name === 'pending') {
            newBalance.remaining = newBalance.total - newBalance.used - newBalance.pending;
        }
        setCurrentLeaveBalance(newBalance);
    };

    const handleSaveBalance = async () => {
        try {
            if (currentLeaveType === 'No Leave Assigned') {
                return;
            }

            const selectedLeaveBalance = currentEmployee.leaveBalances.find(lb => lb.type === currentLeaveType);

            if (selectedLeaveBalance && selectedLeaveBalance.leave_type_id) {
                const payload = {
                    emp_leave_balance_id: selectedLeaveBalance.leave_type_id,
                    max_days: currentLeaveBalance.total,
                    balance_days: currentLeaveBalance.remaining,
                    user_id: currentEmployee.id
                };

                const response = await putData(API_ENDPOINTS.updateEmployeeLeaveBalance, payload);;

                if (response.status === 'SUCCESS') {
                    updateEmployeeLeaveBalance(selectedLeaveBalance.leave_type_id);
                    setSnackbar({ open: true, message: 'Leave balance updated successfully', severity: 'success' });
                    handleCloseDialog();
                } else {
                    setSnackbar({ open: true, message: response.message || 'Failed to update leave balance', severity: 'error' });
                }
            }
        } catch (error) {
            setSnackbar({ open: true, message: error?.response?.data?.message || 'An error occurred while updating leave balance', severity: 'error' });
            return;
        }
    };

    const updateEmployeeLeaveBalance = (leaveTypeId) => {
        const updatedEmployee = { ...currentEmployee };
        const existingBalanceIndex = updatedEmployee.leaveBalances.findIndex(lb =>
            lb.leave_type_id === leaveTypeId
        );
        if (existingBalanceIndex >= 0) {
            updatedEmployee.leaveBalances[existingBalanceIndex] = {
                ...updatedEmployee.leaveBalances[existingBalanceIndex],
                total: currentLeaveBalance.total,
                used: currentLeaveBalance.used,
                pending: currentLeaveBalance.pending,
                remaining: currentLeaveBalance.remaining
            };
        }
        setEmployees(employees.map(emp =>
            emp.id === updatedEmployee.id ? updatedEmployee : emp
        ));
    };

    const getProgressColor = (remaining, total) => {
        if (total === 0) return theme.palette.grey[500];
        const percentage = (remaining / total) * 100;
        if (percentage <= 20) return theme.palette.error.main;
        if (percentage <= 40) return theme.palette.warning.main;
        return theme.palette.success.main;
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>

            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    Leave Balances
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Track and manage employee leave allocations across departments
                </Typography>
            </Box>
            {/* Filters */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                mb: 3,
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    Employee Leave Records
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search employee..."
                        value={searchTerm}
                        onChange={handleSearch}
                        sx={{ width: { xs: '100%', sm: 240 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ width: { xs: '100%', sm: 180 } }}>
                        <InputLabel>Department</InputLabel>
                        <Select
                            value={selectedDepartment}
                            onChange={handleDepartmentChange}
                            label="Department"
                        >
                            {departments.map(dept => (
                                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        size="small"
                        sx={{ height: 40 }}
                        onClick={() => fetchLeaveBalances(organizationId)}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {error && (
                <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 1 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {loading && <Loader sx={{ mb: 2 }} />}

            <Card elevation={0} sx={{ mb: 2, border: '1px solid #e0e4e8', borderRadius: 1 }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Leave Type</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>Total</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>Used</TableCell>

                                <TableCell sx={{ fontWeight: 600 }}>Remaining</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredEmployees
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((employee) => {
                                    const selectedBalance = getSelectedLeaveBalance(employee);

                                    return (
                                        <TableRow
                                            key={employee.id}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                                }
                                            }}
                                        >
                                            {/* Employee Info */}
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                                        {employee.avatar}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 500 }}>
                                                            {employee.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {employee.employeeId}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            {/* Department */}
                                            <TableCell>
                                                {employee.department}
                                            </TableCell>

                                            {/* Leave Type Dropdown */}
                                            <TableCell>
                                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                                    <Select
                                                        value={selectedLeaveTypes[employee.id] || employee.leaveBalances[0]?.type || ''}
                                                        onChange={(e) => handleLeaveTypeSelect(employee.id, e.target.value)}
                                                        displayEmpty
                                                        size='small'
                                                        sx={{
                                                            '& .MuiSelect-select': {
                                                                py: 1,
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }
                                                        }}
                                                    >

                                                        {employee.leaveBalances.map((balance) => (
                                                            <MenuItem key={balance.type} value={balance.type}>

                                                                {balance.type}

                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>

                                            {/* Total */}
                                            <TableCell align="center">
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedBalance.total}
                                                </Typography>
                                            </TableCell>

                                            {/* Used */}
                                            <TableCell align="center">
                                                <Typography variant="body2">
                                                    {selectedBalance.used || 0}
                                                </Typography>
                                            </TableCell>

                                            {/* Remaining with Progress */}
                                            <TableCell>
                                                {selectedBalance.type === "Loss of Pay" || selectedBalance.type === "Work From Home" ? (
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {selectedBalance.remaining}
                                                    </Typography>
                                                ) : (
                                                    <Box sx={{ width: '100%', maxWidth: 120 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: selectedBalance.remaining <= 2 ? theme.palette.error.main :
                                                                        selectedBalance.remaining <= 5 ? theme.palette.warning.main :
                                                                            theme.palette.success.main,
                                                                    mr: 1
                                                                }}
                                                            >
                                                                {selectedBalance.remaining ?? 0}
                                                            </Typography>
                                                            {selectedBalance.remaining <= 2 && selectedBalance.total > 0 && (
                                                                <Tooltip title="Low balance">
                                                                    <WarningIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={selectedBalance.total > 0 ? (selectedBalance.remaining / selectedBalance.total) * 100 : 0}
                                                            sx={{
                                                                height: 4,
                                                                borderRadius: 2,
                                                                backgroundColor: alpha(theme.palette.grey[500], 0.2),
                                                                '& .MuiLinearProgress-bar': {
                                                                    backgroundColor: getProgressColor(selectedBalance.remaining, selectedBalance.total),
                                                                    borderRadius: 2
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    <Tooltip title="Edit Leave Balances">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleOpenDialog(employee)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                            {/* Empty state */}
                            {filteredEmployees.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                            <SearchIcon sx={{ fontSize: 48, color: theme.palette.grey[400] }} />
                                            <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                                                No employees found matching your search.
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                                                Try adjusting your search or filter criteria.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredEmployees.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        borderTop: '1px solid #e0e4e8',
                        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                            my: 1
                        }
                    }}
                />

                <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {Math.min(rowsPerPage, filteredEmployees.length - page * rowsPerPage)} of {filteredEmployees.length} employees
                    </Typography>
                </Box>
            </Card>
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                {currentEmployee && (
                    <>
                        <DialogTitle component="div" sx={{
                            borderBottom: '1px solid #e0e4e8',
                            pb: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Typography variant="h6">Edit Leave Balance</Typography>
                            <IconButton onClick={handleCloseDialog} size="small">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3, px: 3 }}>
                            <Box sx={{ display: 'flex', mb: 3, alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                                    {currentEmployee.avatar}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                        {currentEmployee.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {currentEmployee.employeeId} • {currentEmployee.department}
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Leave Type</InputLabel>
                                        <Select
                                            value={currentLeaveType}
                                            onChange={handleLeaveTypeChange}
                                            label="Leave Type"
                                        >
                                            {currentEmployee.leaveBalances.map(balance => (
                                                <MenuItem key={balance.type} value={balance.type}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box
                                                            sx={{
                                                                width: 10,
                                                                height: 10,
                                                                borderRadius: '50%',
                                                                bgcolor: leaveTypeColors[balance.type] || theme.palette.grey[500]
                                                            }}
                                                        />
                                                        {balance.type}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                            {organizationLeaveTypes
                                                .filter(type => !currentEmployee.leaveBalances.some(balance =>
                                                    balance.type === type.leave_type))
                                                .map(type => (
                                                    <MenuItem key={type.id} value={type.leave_type}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AddIcon fontSize="small" color="primary" />
                                                            {type.leave_type}
                                                        </Box>
                                                    </MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Conditionally render fields based on leave type */}
                                {currentLeaveType !== "Loss of Pay" && currentLeaveType !== "Work From Home" && (
                                    <>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <TextField
                                                name="total"
                                                label="Total Allocation"
                                                type="number"
                                                value={currentLeaveBalance.total}
                                                onChange={handleBalanceChange}
                                                fullWidth
                                                InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <TextField
                                                name="used"
                                                label="Used"
                                                type="number"
                                                value={currentLeaveBalance.used}
                                                onChange={handleBalanceChange}
                                                fullWidth
                                                InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                name="remaining"
                                                label="Remaining Balance"
                                                type="number"
                                                value={currentLeaveBalance.remaining}
                                                disabled
                                                fullWidth
                                                InputProps={{ inputProps: { min: 0 } }}
                                                InputLabelProps={{
                                                    shrink: currentLeaveBalance.remaining !== '' && currentLeaveBalance.remaining !== null && currentLeaveBalance.remaining !== undefined ? true : undefined
                                                }}
                                                sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05) }}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {/* Show message for Loss of Pay/Work From Home */}
                                {(currentLeaveType === "Loss of Pay" || currentLeaveType === "Work From Home") && (
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            {currentLeaveType} does not require to add leave balance.
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>

                            <Box sx={{
                                mt: 3,
                                p: 2,
                                bgcolor: alpha(theme.palette.info.main, 0.05),
                                borderRadius: 1,
                                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                            }}>
                                <Typography variant="body2" sx={{ color: theme.palette.info.dark }}>
                                    <strong>Note:</strong> Changes to leave balances will be logged in the system audit trail.
                                    {currentLeaveType !== "Loss of Pay" && currentLeaveType !== "Work From Home" && (
                                        " The remaining balance is automatically calculated."
                                    )}
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e4e8' }}>
                            <Button
                                onClick={handleCloseDialog}
                                variant="outlined"
                                color="inherit"
                                sx={{ mr: 1, textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSaveBalance}
                                variant="contained"
                                color="primary"
                                disabled={currentLeaveType === "Loss of Pay" || currentLeaveType === "Work From Home"}
                                sx={{ textTransform: 'none' }}
                            >
                                Save Changes
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={4000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })} 
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EmployeesLeaveBalanceList;